'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function EstetikIslemlerinEtkisiniAzaltanHastaliklarPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/estetik-islemlerin-etkisini-azaltan-hastaliklar';
  const pageTitle = 'Estetik İşlemlerin Etkisini Azaltan Hastalıklar - Botoks, Dolgu Etkisi';
  const pageDescription = 'Botoks, dolgu, mezoterapi gibi estetik işlemler herkes için aynı sonucu vermez. Tansiyon, diyabet, tiroid ve bazı ilaçlar işlemin etkisini azaltabilir. Detaylar burada.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Estetik İşlemlerin Etkisini Azaltan Hastalıklar', href: '/tr/blog/estetik-islemlerin-etkisini-azaltan-hastaliklar' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Estetik işlemlerin etkisini azaltan hastalıklar değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Estetik Etki
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Estetik İşlemlerin Etkisini Azaltan Hastalıklar
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>26 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>10 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Estetik işlemler, günümüzde hem kadınlar hem erkekler tarafından sıkça tercih edilen güvenli uygulamalardır. Ancak her bireyde aynı sonucu vermeyebilir. Bazı kronik hastalıklar, ilaç kullanımları ve metabolik farklılıklar, yapılan işlemlerin etkisini azaltabilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed font-medium">
                Bu nedenle işlem öncesi hastanın genel sağlık durumu mutlaka değerlendirilmelidir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Tansiyon (Hipertansiyon) ve Kalp Hastalıkları
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüksek tansiyon, damar elastikiyetini ve dolaşımı etkiler. Bu durum, cilt altına enjekte edilen botoks veya dolgu maddesinin dağılımını ve emilimini değiştirir. Ayrıca tansiyon ilaçlarından bazıları (örneğin beta blokerler) sinir iletimini etkileyerek botoksun kas üzerindeki etkisini zayıflatabilir.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div>
                    <p className="text-blue-800 font-medium mb-2">Öneri:</p>
                    <ul className="list-disc list-inside text-blue-800 space-y-1">
                      <li>İşlem öncesi ilaçlar mutlaka doktora bildirilmelidir.</li>
                      <li>Kan basıncı kontrol altında değilse estetik işlem ertelenmelidir.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Diyabet (Şeker Hastalığı)
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Diyabet hastalarında doku iyileşmesi yavaşlar ve mikrosirkülasyon zayıflar. Bu da dolgu, mezoterapi ve PRP gibi işlemlerin etkisinin gecikmesine neden olur. Ayrıca, yüksek kan şekeri seviyesi kolajen sentezini azaltır, cilt yenilenmesini yavaşlatır.
              </p>

              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etki Alanı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Sonuç
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Mezoterapi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Etki daha geç başlar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Dolgu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kalıcılık süresi kısalır</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">PRP</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yenilenme cevabı azalır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div>
                    <p className="text-blue-800 font-medium mb-2">Öneri:</p>
                    <p className="text-blue-800">İşlem öncesi kan şekeri dengelenmeli, açlık-tokluk durumuna dikkat edilmelidir.</p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Tiroid Hastalıkları
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Özellikle hipotiroidi (tiroid az çalışması) durumunda cilt kuru ve mat hale gelir. Bu da dolgu ve botoksun beklenen estetik etkisini azaltır. Tiroid hormonlarının düşük olması, hücre yenilenme hızını düşürür.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div>
                    <p className="text-blue-800 font-medium mb-2">Öneri:</p>
                    <ul className="list-disc list-inside text-blue-800 space-y-1">
                      <li>Tiroid değerleri (TSH, T3, T4) normal sınırlarda olmalıdır.</li>
                      <li>Aksi durumda işlem sonrası etki daha kısa sürebilir.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Böbrek Hastalıkları ve Diüretik Kullanımı
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Diüretik (idrar söktürücü) ilaçlar vücuttan su atılmasına neden olur. Bu da ciltte nem kaybı ve hacim azalmasına yol açar. Botoks, dolgu veya mezoterapi sonrası nemsiz ciltte etkiler kısa sürer, sonuçlar istenen düzeye ulaşmayabilir.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div>
                    <p className="text-blue-800 font-medium mb-2">Öneri:</p>
                    <p className="text-blue-800">İşlem öncesi bol su tüketimi, elektrolit dengesi sağlanmalıdır.</p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Otoimmün ve Romatizmal Hastalıklar
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Lupus, romatoid artrit gibi bağışıklık sisteminin aşırı çalıştığı hastalıklarda, vücut enjekte edilen maddelere farklı tepki gösterebilir. Bu durum bazen hızlı emilim, bazen de lokal inflamasyon olarak kendini gösterir.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div>
                    <p className="text-blue-800 font-medium mb-2">Öneri:</p>
                    <p className="text-blue-800">İmmünsüpresif ilaç kullanan hastalarda işlem planlaması dikkatle yapılmalıdır.</p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Kronik Stres ve Uyku Bozuklukları
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Stres hormonları (özellikle kortizol) cilt yenilenmesini olumsuz etkiler. Uyku düzensizliği, kolajen üretimini azaltır. Bu nedenle işlem sonucu kısa sürede kaybolabilir.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div>
                    <p className="text-blue-800 font-medium mb-2">Öneri:</p>
                    <p className="text-blue-800">İşlem öncesi en az 1 hafta kaliteli uyku, düzenli beslenme ve yeterli sıvı alımı sağlanmalıdır.</p>
                  </div>
                </div>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Estetik işlemlerin etkisini azaltan hastalıklar"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Tansiyon hastaları botoks yaptırabilir mi?</h3>
                  <p className="text-gray-700">Evet, ama tansiyon kontrol altındaysa ve doktor onayı varsa güvenlidir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Diyabetlilerde dolgu etkisi neden kısa sürer?</h3>
                  <p className="text-gray-700">Yavaş doku yenilenmesi ve düşük kolajen üretimi nedeniyle kalıcılık süresi azalır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Tiroid hastalarında estetik işlem güvenli mi?</h3>
                  <p className="text-gray-700">Evet, ama hormon dengesi sağlanmış olmalıdır; aksi halde sonuç kısa ömürlü olur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Diüretik ilaç kullananlarda neye dikkat edilmeli?</h3>
                  <p className="text-gray-700">Cilt nemi düşük olacağından işlem öncesi bol su içmek gerekir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Otoimmün hastalıklar estetik işlemlere engel midir?</h3>
                  <p className="text-gray-700">Tamamen yasak değildir, ancak mutlaka doktor kontrolünde planlanmalıdır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>Mayo Clinic – "Impact of Systemic Diseases on Aesthetic Procedures"</li>
                <li>Türk Dermatoloji Derneği – Sistemik Hastalıklar ve Estetik Uygulama Kılavuzu</li>
                <li>Harvard Health Publishing – "Chronic Conditions and Cosmetic Outcomes"</li>
                <li>European Journal of Aesthetic Medicine (2024) – "Endocrine Influence on Injectables"</li>
                <li>ISAPS Global Survey 2024 – "Patient Safety and Systemic Disease Management"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#mezoterapi</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#estetiketkisi</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#tansiyon</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#diyabet</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#tiroid</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#otoimmün</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Estetik İşlem Öncesi Sağlık Değerlendirmesi</h3>
              <p className="mb-6">Mevcut hastalıklarınız ve kullandığınız ilaçlar hakkında detaylı bilgi almak için uzman doktorumuzla görüşün.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Estetik işlem öncesi sağlık değerlendirmesi hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-red-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sıvı Yüz Germe Nedir?</h4>
              <p className="text-gray-600 text-sm">Sıvı yüz germe işlemi ve kullanılan içerikler hakkında bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/noroloji-doktorlari-neden-botoks-onermez"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Nöroloji Doktorları Neden Botoks Önermez?</h4>
              <p className="text-gray-600 text-sm">Nöroloji uzmanlarının botoks konusundaki yaklaşımları.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

